import type { ShadowShape } from "../api/types";
import {
  binLabel,
  money,
  SHAPE_FIRST_PROFIT_BIN,
} from "../lib/shape";

/**
 * Гистограмма счёта: из каких сделок он сложился.
 *
 * # Почему картинка, а не ещё три числа
 *
 * Числа рядом с ней уже есть (медиана, счёт без трёх лучших), и по отдельности
 * каждое отвечает лишь на свой вопрос. Форму же читают одним взглядом, и
 * различие, ради которого всё затевалось, видно именно в ней: у расхождения
 * модели и стакана масса стоит в САМОЙ ЛЕВОЙ корзине — «потеряли весь билет», —
 * а счёт вытянут длинным правым хвостом; у почти решённого масса в +0…+5, и
 * левого края почти нет. Это два разных способа получить плюс, и таблица чисел
 * их не показывает.
 *
 * # Почему inline-SVG, а не библиотека графиков
 *
 * Картинка размером с строку таблицы, десять столбиков, без осей и подсказок
 * сверх title. Тянуть под это графическую библиотеку значило бы добавить сотни
 * килобайт к бандлу ради того, что рисуется десятью прямоугольниками.
 *
 * # Масштаб — по САМОЙ ВЫСОКОЙ корзине этой строки
 *
 * То есть высота столбика говорит о доле внутри группы, а не о числе сделок.
 * Иначе группа с 771 сделкой раздавила бы группу с 54 в ноль, и сравнивать
 * ФОРМЫ (ради чего гистограмма и нужна) стало бы нельзя. Абсолютное число едет
 * рядом отдельной колонкой и в подсказке.
 */
export function PnLShape({ shape }: { shape: ShadowShape | undefined }) {
  if (!shape || shape.n === 0) {
    return <span className="text-[var(--color-muted)]">—</span>;
  }
  const max = Math.max(...shape.bins, 1);
  const w = 6;
  const gap = 1;
  const h = 16;
  const title = [
    `${shape.n} сделок, счёт ${money(shape.sum)}`,
    `медиана ${money(shape.median)}`,
    `без трёх лучших ${money(shape.without_top3)}`,
    `лучшая ${money(shape.top1)}`,
    "",
    ...shape.bins
      .map((n, i) => (n === 0 ? "" : `${binLabel(i)}: ${n}`))
      .filter(Boolean),
  ].join("\n");

  return (
    <svg
      width={shape.bins.length * (w + gap)}
      height={h}
      className="inline-block align-middle"
      role="img"
      aria-label={`форма счёта: ${shape.n} сделок`}
    >
      <title>{title}</title>
      {shape.bins.map((n, i) => {
        // Пустая корзина рисуется ЧЁРТОЧКОЙ в один пиксель, а не пропускается:
        // без неё десять корзин превращаются в пять, и глаз теряет, где ноль.
        const bar = n === 0 ? 1 : Math.max(2, Math.round((n / max) * h));
        return (
          <rect
            key={i}
            x={i * (w + gap)}
            y={h - bar}
            width={w}
            height={bar}
            fill={
              n === 0
                ? "var(--color-border)"
                : i < SHAPE_FIRST_PROFIT_BIN
                  ? "var(--color-ask)"
                  : "var(--color-bid)"
            }
            opacity={n === 0 ? 1 : 0.85}
          />
        );
      })}
    </svg>
  );
}
